import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "../../shared/services/api";
// v2.79 Phase 2 — Subscribe to the `config` data-sync domain so admin
// edits to filters / locations / plans push live across every open tab
// instead of waiting for a focus/visibility re-fetch.
import { useDataSync } from "../../lib/dataSync";
// v2-listing-schema — Central Listing Schema (form fields + search filters)
import {
  LISTING_SCHEMA_VERSION,
  DEFAULT_LISTING_SCHEMA,
  fetchListingSchema,
  normalizeListingSchema,
  purgeStaleListingSchemaCache,
  readCachedSchema,
  writeCachedSchema,
} from "../../lib/listingSchema";

/**
 * ConfigContext — Single Source of Truth (v2.20 + v2-listing-schema)
 * ------------------------------------------------------------------
 * One bootstrap call to /api/config hydrates global state:
 *   • settings — { free_mode, max_free_listings, plans_enabled }
 *   • filters  — active filter catalog (dropdown/toggle/slider) with dynamic options
 *   • plans    — { client?: [...], dealer?: [...] } (role-filtered server-side)
 *   • viewer   — { role, tier, is_authenticated }
 *
 * v2-listing-schema additions:
 *   • listingSchema       — { version, schemaType, fields, filters }
 *   • formFields          — alias for listingSchema.fields
 *   • searchFilters       — alias for listingSchema.filters
 *   • schemaLoading       — true while the schema fetch is in flight
 *   • schemaError         — string|null, NEVER user-facing (silent fallback)
 *   • refreshListingSchema()
 *
 * Components consume via `useConfig()`. Call `refresh()` after admin edits
 * to re-hydrate. The schema fetch falls back to DEFAULT_LISTING_SCHEMA so
 * the UI never breaks when the backend is unreachable.
 */
const ConfigContext = createContext({
  loading: true,
  settings: { free_mode: true, max_free_listings: 10, plans_enabled: true },
  filters: [],
  locations: [],
  plans: {},
  viewer: { role: null, tier: null, is_authenticated: false },
  listingSchema: DEFAULT_LISTING_SCHEMA,
  formFields: DEFAULT_LISTING_SCHEMA.fields,
  searchFilters: DEFAULT_LISTING_SCHEMA.filters,
  schemaLoading: true,
  schemaError: null,
  schemaVersion: LISTING_SCHEMA_VERSION,
  refresh: () => {},
  refreshListingSchema: () => {},
});

// One-time per app load: purge legacy localStorage so production users
// force a clean refetch instead of consuming stale cached filter data.
// Runs at module import (idempotent, version-checked).
purgeStaleListingSchemaCache();

export function ConfigProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    settings: { free_mode: true, max_free_listings: 10, plans_enabled: true },
    filters: [],
    locations: [],
    plans: {},
    viewer: { role: null, tier: null, is_authenticated: false },
  });

  // v2-listing-schema — separate state slice so we can refresh schema
  // independently of /api/config (admin edits the schema → only
  // refetch /api/admin/listing-schema, not the whole bootstrap).
  // On first mount we hydrate from localStorage (if version matches)
  // so the UI has SOMETHING to render while the network call lands.
  const cached = useRef(readCachedSchema());
  const [schemaState, setSchemaState] = useState({
    listingSchema: cached.current || DEFAULT_LISTING_SCHEMA,
    schemaLoading: true,
    schemaError: null,
  });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/config", { params: { _t: Date.now() } });
      setState({ ...r.data, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  // Refresh just the listing schema — used after admin saves the schema
  // or when ConfigContext detects an admin / login state change.
  const refreshListingSchema = useCallback(async () => {
    const isAdmin = (state?.viewer?.role === "admin") || !!localStorage.getItem("auth_token");
    setSchemaState((s) => ({ ...s, schemaLoading: true }));
    const { schema, source, error } = await fetchListingSchema(api, { tryAdmin: isAdmin });
    const norm = normalizeListingSchema(schema);
    setSchemaState({ listingSchema: norm, schemaLoading: false, schemaError: error || null });
    // Cache normalised schema for next mount
    writeCachedSchema(norm);
    // Log the source for diagnostics — never user-facing
    if (error) console.warn("[listing-schema] using fallback (source=" + source + ")");
  }, [state?.viewer?.role]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { refreshListingSchema(); }, [refreshListingSchema]);

  // v2.79 Phase 2 — Real-time refresh when any admin filter / location /
  // plan mutation is broadcast over the data.sync channel. Replaces the
  // previous focus/visibility-only fallback for these specific domains.
  useDataSync(["config"], () => {
    refresh();
    refreshListingSchema();
  });

  // Re-fetch whenever the auth_token changes (login/logout) — different viewer means
  // potentially different plans (dealer vs client) + different visibility.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "auth_token") {
        refresh();
        refreshListingSchema();
      }
    };
    // Also re-hydrate when tab/window regains focus or becomes visible — picks up
    // admin changes made in another tab without requiring a hard refresh.
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, refreshListingSchema]);

  // v2.71.24 — Memoize so every alt-tab focus doesn't re-render every
  // useConfig() consumer (100+ across the app).
  const value = useMemo(() => ({
    ...state,
    ...schemaState,
    formFields: schemaState.listingSchema?.fields || [],
    searchFilters: schemaState.listingSchema?.filters || [],
    schemaVersion: LISTING_SCHEMA_VERSION,
    refresh,
    refreshListingSchema,
  }), [state, schemaState, refresh, refreshListingSchema]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export const useConfig = () => useContext(ConfigContext);
